/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useCallbackProfileForm.ts (Callback Profiles)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Callback Profile form state, validation, and submission.
    Handles create, update, and duplicate operations.

────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createCallbackProfile, updateCallbackProfile } from '../api/callbackProfilesApi';
import { FormState } from '../types';
import { getDefaultFormState } from '../utils/formDefaults';
import { validateForm } from '../utils/validators';
import { buildSettingsFromFormState } from '../utils/settingsHelpers';

/**
 * Hook for managing Callback Profile form
 */
export function useCallbackProfileForm(
  id: string | undefined,
  duplicateId: string | null,
  initialMappedData: { formState: FormState; extraSettings: Record<string, unknown> } | null
) {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();

  const isEditMode = Boolean(id && id !== 'new');
  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [originalFormState, setOriginalFormState] = useState<FormState>(defaultFormState);
  const [extraSettings, setExtraSettings] = useState<Record<string, unknown>>({});
  const [originalExtraSettings, setOriginalExtraSettings] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const lastInitializedIdRef = useRef<string | null>(null);

  // Initialize form data when initialMappedData changes
  useEffect(() => {
    const currentId = duplicateId || id || 'new';

    if (initialMappedData) {
      // Update when we have mapped data for a different ID, or when transitioning from null to data
      const isDifferentId = lastInitializedIdRef.current !== currentId;
      const isDataLoaded = lastInitializedIdRef.current === null && isEditMode;
      const isNameDifferent = formState.name !== initialMappedData.formState.name &&
        initialMappedData.formState.name !== '';

      if (isDifferentId || isDataLoaded || isNameDifferent) {
        lastInitializedIdRef.current = currentId;
        // Create new objects to ensure React detects the change
        setFormState({ ...initialMappedData.formState });
        setOriginalFormState(duplicateId ? getDefaultFormState() : { ...initialMappedData.formState });
        setExtraSettings({ ...initialMappedData.extraSettings });
        setOriginalExtraSettings(duplicateId ? {} : { ...initialMappedData.extraSettings });
        setErrors({});
      }
    } else if (!isEditMode && !duplicateId) {
      // Reset to defaults for new form
      if (lastInitializedIdRef.current !== 'new') {
        lastInitializedIdRef.current = 'new';
        const defaultState = getDefaultFormState();
        setFormState(defaultState);
        setOriginalFormState({ ...defaultState });
        setExtraSettings({});
        setOriginalExtraSettings({});
        setErrors({});
      }
    } else if (isEditMode && !initialMappedData) {
      // Clear ref when switching to edit mode but data not loaded yet
      if (lastInitializedIdRef.current && lastInitializedIdRef.current !== currentId) {
        lastInitializedIdRef.current = null;
      }
    }
  }, [initialMappedData, isEditMode, duplicateId, id, defaultFormState, formState.name]);

  const handleFieldChange = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleExtraSettingsChange = useCallback((settings: Record<string, unknown>) => {
    setExtraSettings(settings);
  }, []);

  const hasChanges = useMemo(() => {
    const formChanged = Object.keys(formState).some((key) => {
      const formKey = key as keyof FormState;
      return formState[formKey] !== originalFormState[formKey];
    });

    const settingsChanged = JSON.stringify(extraSettings) !== JSON.stringify(originalExtraSettings);

    return formChanged || settingsChanged;
  }, [formState, extraSettings, originalFormState, originalExtraSettings]);

  // Handle save - Always-Active Save Standard: always attempt to save
  const handleSave = useCallback(async () => {
    const trimmedName = formState.name.trim();
    if (!trimmedName) {
      showError(t('callbackProfiles.messages.validationError'));
      throw new Error(t('callbackProfiles.messages.validationError'));
    }

    const validationErrors = validateForm(formState, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const errorMsgs = Object.values(validationErrors).filter(msg => typeof msg === 'string').join(' • ');
      const valErrorTitle = t('callbackProfiles.messages.validationError', 'Please fix validation errors');
      showError(`${valErrorTitle}${errorMsgs ? ': ' + errorMsgs : ''}`);
      throw new Error(valErrorTitle);
    }

    setIsSaving(true);
    try {
      const settings = buildSettingsFromFormState(formState, extraSettings);

      const payload = {
        name: trimmedName,
        description: formState.description.trim() || null,
        enabled: formState.enabled,
        settings
      };

      const result = isEditMode
        ? await updateCallbackProfile(id!, payload)
        : await createCallbackProfile(payload);

      // Update original state
      setOriginalFormState({ ...formState });
      setOriginalExtraSettings({ ...extraSettings });

      showSuccess(t('callbackProfiles.messages.saveSuccess'));

      // Return saved data for navigation
      return {
        id: result?.id,
        uuid: result?.id,
        ...result
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('callbackProfiles.messages.saveError');
      showError(message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [formState, extraSettings, hasChanges, id, isEditMode, showError, showSuccess, showInfo, t]);

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormState({ ...originalFormState });
      setExtraSettings({ ...originalExtraSettings });
      setErrors({});
      return;
    }

    const defaultState = getDefaultFormState();
    setFormState(defaultState);
    setOriginalFormState({ ...defaultState });
    setExtraSettings({});
    setOriginalExtraSettings({});
    setErrors({});
  }, [isEditMode, originalFormState, originalExtraSettings]);

  return {
    formState,
    extraSettings,
    errors,
    isSaving,
    hasChanges,
    handleFieldChange,
    handleExtraSettingsChange,
    handleSave,
    handleCancel
  };
}

